"use client";

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { Criterion } from "@/generated/fastapi";
import usePageSearch from "./usePageSearch";

type CriteriaSelectionStore = Record<string, Criterion[]>;

const STORAGE_KEY = "search-criteria-selection-v2";

let cachedSelectionStore: CriteriaSelectionStore = {};
let hasLoadedSelectionStore = false;
const selectionListeners = new Set<() => void>();

function readStoredSelection(): CriteriaSelectionStore {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as CriteriaSelectionStore;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
}

function ensureSelectionStoreLoaded() {
  if (hasLoadedSelectionStore) return;
  hasLoadedSelectionStore = true;
  cachedSelectionStore = readStoredSelection();
}

function persistSelectionStore(nextStore: CriteriaSelectionStore) {
  cachedSelectionStore = nextStore;

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextStore));
    } catch {
      // Ignore persistence failures in constrained environments.
    }
  }

  selectionListeners.forEach((listener) => listener());
}

function subscribeToSelectionStore(listener: () => void) {
  ensureSelectionStoreLoaded();
  selectionListeners.add(listener);
  return () => {
    selectionListeners.delete(listener);
  };
}

function getSelectionSnapshot() {
  ensureSelectionStoreLoaded();
  return cachedSelectionStore;
}

export default function useSearchCriteriaSelection() {
  const pathname = usePathname();
  const { pageSearch } = usePageSearch();
  const setSearchQuery = pageSearch.setSearchQuery;

  const selectedCriteriaByPage = useSyncExternalStore(
    subscribeToSelectionStore,
    getSelectionSnapshot,
    () => ({} as CriteriaSelectionStore),
  ) as CriteriaSelectionStore;

  const currentPath = pathname ?? "";
  const selectedCriteria = useMemo(
    () => (currentPath ? selectedCriteriaByPage[currentPath] || [] : []),
    [currentPath, selectedCriteriaByPage],
  );

  const setSelectedCriteria = useCallback(
    (criteria: Criterion[]) => {
      if (!currentPath) return;

      persistSelectionStore({
        ...selectedCriteriaByPage,
        [currentPath]: criteria,
      });
    },
    [currentPath, selectedCriteriaByPage],
  );

  const selectedCriterionIds = useMemo(
    () => selectedCriteria.map((criterion) => criterion.id),
    [selectedCriteria],
  );

  const appendCriteria = useCallback(
    (criteria: Criterion[]) => {
      if (!criteria || criteria.length === 0 || !currentPath) return;

      const existingIds = new Set(selectedCriteria.map((item) => item.id));
      const toAdd = criteria.filter(
        (criterion) => !existingIds.has(criterion.id),
      );
      if (toAdd.length === 0) return;

      const nextSelection = [...selectedCriteria, ...toAdd];
      setSelectedCriteria(nextSelection);
      setSearchQuery(
        nextSelection.map((criterion) => criterion.mindstring).join(" ").trim(),
      );
    },
    [currentPath, selectedCriteria, setSearchQuery, setSelectedCriteria],
  );

  const removeCriteria = useCallback(
    (criteria: Criterion[]) => {
      if (!criteria || criteria.length === 0 || !currentPath) return;

      const idsToRemove = new Set(criteria.map((criterion) => criterion.id));
      const nextSelection = selectedCriteria.filter(
        (criterion) => !idsToRemove.has(criterion.id),
      );

      if (nextSelection.length === selectedCriteria.length) return;

      setSelectedCriteria(nextSelection);
      setSearchQuery(
        nextSelection.map((criterion) => criterion.mindstring).join(" ").trim(),
      );
    },
    [currentPath, selectedCriteria, setSearchQuery, setSelectedCriteria],
  );

  const clearCriteria = useCallback(
    (criteria?: Criterion[]) => {
      if (!currentPath) return;

      if (criteria && criteria.length > 0) {
        removeCriteria(criteria);
        return;
      }

      setSelectedCriteria([]);
      setSearchQuery("");
    },
    [currentPath, removeCriteria, setSearchQuery, setSelectedCriteria],
  );

  const restoreCriteria = useCallback(
    (criteria: Criterion[]) => {
      if (!currentPath) return;

      setSelectedCriteria(criteria);
      setSearchQuery(
        criteria.map((criterion) => criterion.mindstring).join(" ").trim(),
      );
    },
    [currentPath, setSearchQuery, setSelectedCriteria],
  );

  const toggleCriterion = useCallback(
    (criterion: Criterion) => {
      if (!currentPath) return;

      const isSelected = selectedCriteria.some(
        (item) => item.id === criterion.id,
      );
      if (isSelected) {
        removeCriteria([criterion]);
        return;
      }

      appendCriteria([criterion]);
    },
    [appendCriteria, currentPath, removeCriteria, selectedCriteria],
  );

  const isCriterionSelected = useCallback(
    (criterionId: string) => {
      if (!currentPath) return false;
      return selectedCriteria.some((criterion) => criterion.id === criterionId);
    },
    [currentPath, selectedCriteria],
  );

  const selectedCriteriaCount = selectedCriteria.length;

  return {
    pathname,
    selectedCriteria,
    selectedCriterionIds,
    selectedCriteriaCount,
    appendCriteria,
    removeCriteria,
    clearCriteria,
    restoreCriteria,
    toggleCriterion,
    isCriterionSelected,
  };
}
